import { useMemo, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import type { MediaListItem } from '../api/entries'
import { listUserMedia, uploadUserMedia, deleteUserMedia } from '../api/entries'
import { AuthenticatedMedia } from '../components/Entry/AuthenticatedMedia'
import { usePageHeader } from '../hooks/usePageHeader'
import { formatDateTime } from '../utils/dates'
import { useUsername } from '../hooks/useUsername'

function formatBytes(n: number): string {
  if (n < 1024) return `${n} b`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kb`
  return `${(n / (1024 * 1024)).toFixed(1)} mb`
}

export function MediaLibrary() {
  const username = useUsername()
  const [items, setItems] = useState<MediaListItem[]>([])
  const [storageUsed, setStorageUsed] = useState(0)
  const [storageQuota, setStorageQuota] = useState(0)
  const [storageTier, setStorageTier] = useState('')
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [preview, setPreview] = useState<MediaListItem | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!username) return
    listUserMedia(username)
      .then((r) => {
        setItems(r.items)
        setStorageUsed(r.storage_used)
        setStorageQuota(r.storage_quota)
        setStorageTier(r.storage_tier)
      })
      .finally(() => setLoading(false))
  }, [username])

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!username) return
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0) return
    setUploading(true)
    setUploadError('')
    try {
      const results = await Promise.all(files.map((f) => uploadUserMedia(username, f)))
      const newItems: MediaListItem[] = results.map((r) => ({
        id: r.id,
        filename: r.filename,
        mimetype: r.mimetype,
        content_size: r.content_size,
        url: r.url,
        created_at: new Date().toISOString(),
        referenced_in: [],
      }))
      setItems((prev) => [...newItems, ...prev])
      setStorageUsed((prev) => prev + results.reduce((sum, r) => sum + r.content_size, 0))
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'upload failed')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function handleDelete(item: MediaListItem) {
    if (!username) return
    if (!window.confirm(`delete ${item.filename}? this cannot be undone.`)) return
    try {
      await deleteUserMedia(username, item.id)
      setItems((prev) => prev.filter((m) => m.id !== item.id))
      setStorageUsed((prev) => Math.max(0, prev - item.content_size))
    } catch (err) {
      alert(err instanceof Error ? err.message : 'delete failed')
    }
  }

  const titleNode = useMemo(() => (
    username ? (
      <><Link to={`/~${username}`} style={{ color: 'inherit', textDecoration: 'none', opacity: 0.6 }}>~{username}</Link>{' / media'}</>
    ) : 'media'
  ), [username])

  usePageHeader(titleNode)

  if (!username) return null

  return (
    <div style={{ padding: '1rem' }}>

      {!loading && storageQuota > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '0.3rem' }}>
            {formatBytes(storageUsed)} of {formatBytes(storageQuota)} used ({storageTier})
          </div>
          <div style={{ height: 6, background: 'color-mix(in srgb, var(--color-fg) 15%, transparent)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(100, (storageUsed / storageQuota) * 100).toFixed(1)}%`, background: storageUsed / storageQuota > 0.9 ? 'var(--color-1)' : 'var(--color-fg)', borderRadius: 3, transition: 'width 0.3s' }} />
          </div>
        </div>
      )}

      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
          aria-label="upload files"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          style={{ width: 'auto' }}
        >
          + upload file
        </button>
        {uploading && <span style={{ fontSize: '0.85rem', opacity: 0.7 }}>uploading…</span>}
        {uploadError && <span style={{ fontSize: '0.85rem', color: 'var(--color-1)' }}>{uploadError}</span>}
      </div>

      {loading && <p style={{ opacity: 0.5 }}>loading…</p>}

      {!loading && items.length === 0 && (
        <p style={{ opacity: 0.5 }}>no uploaded files</p>
      )}

      <div>
        {items.map((item) => (
          <div
            key={item.id}
            style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', padding: '0.75rem 0', borderBottom: '1px solid color-mix(in srgb, var(--color-fg) 20%, transparent)' }}
          >
            <button
              type="button"
              onClick={() => setPreview(item)}
              style={{ width: 80, height: 80, flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid color-mix(in srgb, var(--color-fg) 20%, transparent)', background: 'none', cursor: 'pointer', padding: 0 }}
              aria-label={`preview ${item.filename}`}
            >
              {item.mimetype.startsWith('image/') ? (
                <AuthenticatedMedia url={item.url} mimetype={item.mimetype} name={item.filename} />
              ) : (
                <span style={{ opacity: 0.5, fontSize: '0.7rem', textAlign: 'center', padding: '0.25rem', wordBreak: 'break-all' }}>{item.mimetype}</span>
              )}
            </button>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 'bold', wordBreak: 'break-all', marginBottom: '0.2rem' }}>{item.filename}</div>
              <div style={{ opacity: 0.6, fontSize: '0.8rem', marginBottom: '0.3rem' }}>
                {formatBytes(item.content_size)} · {item.mimetype} · uploaded {formatDateTime(item.created_at)}
              </div>
              <div style={{ fontSize: '0.8rem', marginBottom: '0.3rem' }}>
                {item.referenced_in.length === 0 ? (
                  <span style={{ opacity: 0.4 }}>not referenced in any entry</span>
                ) : (
                  <>
                    <span>referenced in {item.referenced_in.length} {item.referenced_in.length === 1 ? 'entry' : 'entries'}: </span>
                    {item.referenced_in.map((ref, i) => (
                      <span key={ref.id}>
                        {i > 0 && ', '}
                        <Link to={`/~${username}/entry/${ref.id}`}>{ref.name ?? 'untitled'}</Link>
                      </span>
                    ))}
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleDelete(item)}
                style={{ background: 'transparent', color: 'var(--color-fg)', width: 'auto', fontSize: '0.8rem', opacity: 0.7, padding: '0.1rem 0.4rem' }}
              >
                delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {preview && (
        <div
          onClick={() => setPreview(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem', cursor: 'zoom-out' }}
        >
          <div style={{ maxWidth: '90vw', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <AuthenticatedMedia url={preview.url} mimetype={preview.mimetype} name={preview.filename} />
          </div>
          <p style={{ color: '#fff', opacity: 0.8, fontSize: '0.85rem', marginTop: '0.75rem' }}>{preview.filename}</p>
        </div>
      )}
    </div>
  )
}
